import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CircleAlert, Eye, EyeOff, Mail } from 'lucide-react'
import type { ComponentProps, ReactNode } from 'react'
import type { SocialProvider } from '@/lib/auth/authApi'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

/** 42px field height used across the auth + onboarding screens. */
export const AUTH_FIELD_CLASS = 'h-[42px] rounded-lg bg-card px-3 text-[15px] md:text-[15px] dark:bg-card'
export const AUTH_BUTTON_CLASS = 'h-[42px] rounded-lg text-[15px]'

export function AuthHeader({
  title,
  subtitle,
  icon,
}: {
  title: ReactNode
  subtitle?: ReactNode
  icon?: ReactNode
}) {
  return (
    <div className="space-y-2">
      {icon}
      <h1 className="text-[28px] font-semibold leading-tight tracking-tight">{title}</h1>
      {subtitle ? <p className="text-[15px] text-muted-foreground">{subtitle}</p> : null}
    </div>
  )
}

export function MailIconBadge() {
  return (
    <div className="mb-5 flex size-11 items-center justify-center rounded-xl bg-brand-tint text-brand-tint-foreground">
      <Mail className="size-5" aria-hidden="true" />
    </div>
  )
}

export function FieldError({ id, children }: { id?: string; children: ReactNode }) {
  return (
    <p id={id} role="alert" className="flex items-start gap-1.5 text-sm text-destructive">
      <CircleAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      <span>{children}</span>
    </p>
  )
}

export function AuthLabel({ htmlFor, children }: { htmlFor: string; children: ReactNode }) {
  return (
    <label htmlFor={htmlFor} className="text-sm font-medium">
      {children}
    </label>
  )
}

export function AuthInput({ className, ...props }: ComponentProps<typeof Input>) {
  return <Input className={cn(AUTH_FIELD_CLASS, className)} {...props} />
}

export function PasswordInput({ className, ...props }: Omit<ComponentProps<typeof Input>, 'type'>) {
  const { t } = useTranslation('auth')
  const [visible, setVisible] = useState(false)
  return (
    <div className="relative">
      <Input
        type={visible ? 'text' : 'password'}
        className={cn(AUTH_FIELD_CLASS, 'pr-10', className)}
        {...props}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        className="absolute right-1 top-1/2 flex size-8 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label={visible ? t('gateway.password.hide') : t('gateway.password.show')}
        aria-pressed={visible}
      >
        {visible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
      </button>
    </div>
  )
}

export function OrDivider() {
  const { t } = useTranslation('auth')
  return (
    <div className="flex items-center gap-3 text-xs uppercase tracking-wider text-muted-foreground">
      <span className="h-px flex-1 bg-border" />
      <span>{t('gateway.or')}</span>
      <span className="h-px flex-1 bg-border" />
    </div>
  )
}

/** One button per provider the server offers, in the order given. */
export function SocialButtons({
  providers,
  onSignIn,
  disabled,
}: {
  providers: ReadonlyArray<SocialProvider>
  onSignIn: (provider: SocialProvider) => void
  disabled?: boolean
}) {
  const { t } = useTranslation('auth')
  return (
    <div className={cn('grid gap-2.5', providers.length > 1 ? 'grid-cols-2' : 'grid-cols-1')}>
      {providers.map((provider) => (
        <Button
          key={provider}
          type="button"
          variant="outline"
          className={cn(AUTH_BUTTON_CLASS, 'bg-card dark:bg-card')}
          onClick={() => onSignIn(provider)}
          disabled={disabled}
        >
          {provider === 'google' ? <GoogleIcon /> : <AppleIcon />}
          {t(`social.${provider}`)}
        </Button>
      ))}
    </div>
  )
}

function GoogleIcon() {
  return (
    <svg className="size-4" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  )
}

function AppleIcon() {
  return (
    <svg className="size-4 fill-current" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M17.05 12.04c-.03-2.86 2.34-4.24 2.45-4.31-1.34-1.96-3.42-2.23-4.16-2.26-1.77-.18-3.45 1.04-4.35 1.04-.9 0-2.28-1.02-3.76-.99-1.93.03-3.72 1.12-4.71 2.85-2.01 3.49-.51 8.65 1.45 11.48.96 1.39 2.1 2.95 3.58 2.89 1.44-.06 1.98-.93 3.72-.93 1.73 0 2.22.93 3.74.9 1.54-.03 2.52-1.41 3.46-2.81 1.09-1.61 1.54-3.17 1.56-3.25-.03-.02-2.99-1.15-3.02-4.56zM14.36 3.6c.79-.96 1.32-2.29 1.17-3.6-1.13.05-2.51.76-3.32 1.71-.73.84-1.37 2.2-1.2 3.48 1.26.1 2.55-.64 3.35-1.59z" />
    </svg>
  )
}

/** Text-style link button used for "Change", "Forgot?", "Resend", etc. */
export function TextButton({ className, ...props }: ComponentProps<'button'>) {
  return (
    <button
      type="button"
      className={cn(
        'rounded-sm font-medium text-foreground underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60 disabled:no-underline',
        className,
      )}
      {...props}
    />
  )
}
